import { useState } from 'react';
import {
  type CreatePrintTemplateInput,
  type PrintTemplateDocument,
  type PrintTemplateView,
  createPrintTemplateSchema,
  defaultPrintTemplateDocument,
} from '#shared/types';
import { Banner } from '#client/components/ui/Banner';
import { FormActions } from '#client/components/ui/FormActions';
import { ResourceFormShell } from '#client/components/ui/ResourceFormShell';
import { TextArea } from '#client/components/ui/TextArea';
import { TextField } from '#client/components/ui/TextField';
import { useFormValues } from '#client/hooks/useFormValues';
import { useResourceForm } from '#client/hooks/useResourceForm';
import { RESOURCE, routes } from '#client/lib/routes';
import { PdfFrame } from '../PdfFrame';
import { TemplateSections } from './TemplateSections';
import { usePdfPreview } from './usePdfPreview';

/**
 * The template builder.
 *
 * A form on the left, the actual printed document on the right. The preview is
 * produced by `POST /print/preview`, which runs the same renderer a real print
 * job runs — so it is not a rendering *of* the template, it is the output. The
 * usual WYSIWYG failure, where the editor's idea of the layout slowly parts
 * company with the exporter's, has nowhere to happen here because there is only
 * one layout implementation.
 */
export function TemplateBuilderPage() {
  return (
    <ResourceFormShell<PrintTemplateView>
      path={RESOURCE.printTemplates}
      noun="print template"
      fullWidth
    >
      {({ id, record }) => <TemplateForm id={id} record={record} />}
    </ResourceFormShell>
  );
}

interface TemplateFormProps {
  id?: string | undefined;
  record?: PrintTemplateView | undefined;
}

function TemplateForm({ id, record }: TemplateFormProps) {
  const form = useResourceForm<CreatePrintTemplateInput>({
    path: RESOURCE.printTemplates,
    id,
    schema: createPrintTemplateSchema,
    noun: 'Print template',
  });

  // Initialised once, on mount — ResourceFormShell holds this component back
  // until the record has loaded, so no effect ever copies props into state.
  const { values, set } = useFormValues<CreatePrintTemplateInput>({
    name: record?.name ?? '',
    description: record?.description ?? '',
    document: record?.document ?? defaultPrintTemplateDocument(),
  });

  const preview = usePdfPreview(values.document);
  const [section, setSection] = useState('page');

  /** Replaces one section of the document, leaving the rest alone. */
  function patch<K extends keyof PrintTemplateDocument>(
    key: K,
    changes: Partial<PrintTemplateDocument[K]>,
  ) {
    set('document', { ...values.document, [key]: { ...values.document[key], ...changes } });
  }

  return (
    <form onSubmit={form.handleSubmit(values)} className="c-builder">
      <div className="c-builder__panel">
        {form.formError && <Banner tone="critical">{form.formError}</Banner>}

        <TextField
          label="Template name"
          required
          value={values.name}
          error={form.errors.name}
          onChange={(event) => set('name', event.target.value)}
        />
        <TextArea
          label="Description"
          rows={2}
          value={values.description ?? ''}
          error={form.errors.description}
          onChange={(event) => set('description', event.target.value)}
          helpText="Shown in the template picker on the print screen."
        />

        <TemplateSections
          document={values.document}
          onPatch={patch}
          section={section}
          onSectionChange={setSection}
        />

        <FormActions
          cancelTo={routes.printTemplates}
          submitLabel={id === undefined ? 'Create template' : 'Save template'}
          submitting={form.submitting}
        />
      </div>

      <PdfFrame
        className="c-builder__preview"
        src={preview.url}
        title="Template preview"
        message={preview.url ? undefined : preview.error}
        toolbar={
          <span className="c-builder__status">
            {preview.pending ? 'Rendering…' : 'Sample report'}
            {preview.url && preview.error ? ` · ${preview.error}` : ''}
          </span>
        }
      />
    </form>
  );
}
